import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/features/auth/AuthContext";
import { Avatar } from "@/features/auth/Avatar";
import styles from "./AuthMenu.module.css";

/**
 * Top-right account widget.
 *
 * - Logged out  → a "Visitor" button; opens a menu with Log in / Sign up.
 * - Logged in   → avatar + name + role badge; menu with Profile / Log out.
 *
 * Read access never depends on this — it's purely the entry point to auth.
 */
export function AuthMenu() {
  const { user, status, isAdmin, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Avoid a flash of "Visitor" before we've checked the stored token.
  if (status === "loading") {
    return <div className={styles.placeholder} aria-hidden="true" />;
  }

  function close() {
    setOpen(false);
  }

  function handleLogout() {
    logout();
    close();
    navigate("/");
  }

  return (
    <div className={styles.root}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {user ? (
          <>
            <Avatar avatarKey={user.avatarKey} size={28} />
            <span className={`${styles.name} ${styles.hideOnPhone}`}>{user.name}</span>
            <span className={`${styles.role} ${styles.hideOnPhone} ${isAdmin ? styles.roleAdmin : ""}`}>
              {isAdmin ? "admin" : "user"}
            </span>
          </>
        ) : (
          <>
            <span className={styles.visitorDot} aria-hidden="true" />
            <span className={styles.name}>Visitor</span>
          </>
        )}
      </button>

      {open && (
        <>
          <div className={styles.backdrop} onClick={close} />
          <div className={styles.menu} role="menu">
            {user ? (
              <>
                <div className={styles.menuHeader}>
                  <strong>{user.name}</strong>
                  <span className={styles.menuEmail}>{user.email}</span>
                </div>
                <Link to="/profile" className={styles.menuItem} role="menuitem" onClick={close}>
                  Profile &amp; avatar
                </Link>
                <button
                  type="button"
                  className={styles.menuItem}
                  role="menuitem"
                  onClick={handleLogout}
                >
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className={styles.menuItem} role="menuitem" onClick={close}>
                  Log in
                </Link>
                <Link to="/register" className={styles.menuItem} role="menuitem" onClick={close}>
                  Request access
                </Link>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
